import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NAV_CATEGORIES } from '../api/woocommerce';

const LOGO_URL = '/logo-red.png';
const LOGO_ICON = '/logo-pittogramma-transparent.png';

export default function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/cerca?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setMenuOpen(false);
  };

  return (
    <>
      <style>{`
        .navbar {
          position: absolute;
          top: 0; left: 0;
          width: 100%;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 30px;
        }
        .navbar-logo-full {
          height: 60px;
          width: auto;
          max-width: 240px;
          object-fit: contain;
          display: block;
        }
        .navbar-logo-icon {
          display: none;
          height: 60px;
          width: auto;
          max-width: 75px;
          object-fit: contain;
        }
        .navbar-links {
          display: flex;
          align-items: center;
          gap: 90px;
        }
        .navbar-link {
          font-family: 'Alumni Sans', sans-serif;
          font-weight: 500;
          font-size: 24px;
          color: #fff;
          cursor: pointer;
          transition: color 0.2s;
          white-space: nowrap;
        }
        .navbar-link:hover { color: #d71921; }
        .navbar-cta {
          background: #d71921;
          color: #fff;
          font-family: 'Alumni Sans', sans-serif;
          font-weight: 600;
          font-size: 24px;
          padding: 9px 29px;
          flex-shrink: 0;
          white-space: nowrap;
          transition: opacity 0.2s;
          border: none;
          cursor: pointer;
        }
        .navbar-cta:hover { opacity: 0.85; }
        .navbar-search { position: relative; display: flex; align-items: center; }
        .navbar-search-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          display: flex;
          align-items: center;
        }
        .navbar-search-form {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          display: flex;
          gap: 6px;
          background: #111;
          border: 1px solid #333;
          padding: 8px;
          width: 260px;
        }
        .navbar-search-input {
          flex: 1;
          background: #1b1b1b;
          border: 1px solid #333;
          color: #fff;
          font-family: 'Roboto', sans-serif;
          font-size: 15px;
          padding: 8px 10px;
        }
        .navbar-search-submit {
          background: #d71921;
          color: #fff;
          border: none;
          padding: 0 16px;
          cursor: pointer;
          font-family: 'Alumni Sans', sans-serif;
          font-weight: 600;
          font-size: 18px;
        }
        .mobile-search-form { display: flex; gap: 8px; width: 100%; max-width: 320px; margin-bottom: 4px; }
        .hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          cursor: pointer;
          padding: 4px;
          z-index: 200;
        }
        .hamburger span {
          display: block;
          width: 26px;
          height: 2px;
          background: #fff;
          transition: all 0.3s;
        }
        .mobile-menu {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.97);
          z-index: 150;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          gap: 28px;
          padding: 80px 20px 40px;
          overflow-y: auto;
        }
        .mobile-menu.open { display: flex; }
        .mobile-menu-link {
          font-family: 'Alumni Sans', sans-serif;
          font-weight: 500;
          font-size: 32px;
          color: #fff;
          cursor: pointer;
          text-align: center;
          transition: color 0.2s;
        }
        .mobile-menu-link:hover { color: #d71921; }
        .mobile-close {
          position: absolute;
          top: 28px; right: 28px;
          font-size: 36px;
          color: #fff;
          cursor: pointer;
          background: none;
          border: none;
        }
        .mobile-cta {
          background: #d71921;
          color: #fff;
          font-family: 'Alumni Sans', sans-serif;
          font-weight: 600;
          font-size: 24px;
          padding: 12px 36px;
          margin-top: 12px;
          border: none;
          cursor: pointer;
        }

        @media (max-width: 1700px) {
          .navbar { padding: 18px 40px; }
          .navbar-logo-full { height: 52px; max-width: 200px; }
          .navbar-links { gap: 28px; }
          .navbar-link { font-size: 19px; }
          .navbar-cta { font-size: 19px; padding: 8px 20px; }
        }
        @media (max-width: 1400px) {
          .navbar { padding: 20px 20px; }
          .navbar-links { display: none; }
          .navbar-cta { display: none; }
          .navbar-search { display: none; }
          .hamburger { display: flex; }
          .navbar-logo-full { display: none; }
          .navbar-logo-icon { display: block; }
        }
      `}</style>

      <nav className="navbar">
        <Link to="/">
          <img src={LOGO_URL} alt="Falcon" className="navbar-logo-full" />
          <img src={LOGO_ICON} alt="Falcon" className="navbar-logo-icon" />
        </Link>

        <div className="navbar-links">
          {NAV_CATEGORIES.map(cat => (
            <span
              key={cat}
              className="navbar-link"
              onClick={() => navigate(`/categoria/${cat.toLowerCase().replace(/ & /g,'-').replace(/ /g,'-')}`)}
            >{cat}</span>
          ))}
        </div>

        <div className="navbar-search">
          <button
            className="navbar-search-btn"
            aria-label="Cerca prodotti"
            onClick={() => setSearchOpen(o => !o)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
          {searchOpen && (
            <form className="navbar-search-form" onSubmit={submitSearch}>
              <input
                type="text"
                className="navbar-search-input"
                placeholder="Cerca prodotti..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoFocus
              />
              <button type="submit" className="navbar-search-submit">Cerca</button>
            </form>
          )}
        </div>

        <button className="navbar-cta" onClick={() => navigate({ pathname: '/', hash: '#contatti' })}>Contattaci Ora</button>

        <div className="hamburger" onClick={() => setMenuOpen(true)}>
          <span /><span /><span />
        </div>
      </nav>

      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <button className="mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
        <img src={LOGO_URL} alt="Falcon" style={{height:50,marginBottom:20,objectFit:'contain'}} />
        <form className="mobile-search-form" onSubmit={submitSearch}>
          <input
            type="text"
            className="navbar-search-input"
            placeholder="Cerca prodotti..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <button type="submit" className="navbar-search-submit">Cerca</button>
        </form>
        {NAV_CATEGORIES.map(cat => (
          <span key={cat} className="mobile-menu-link" onClick={() => {
            setMenuOpen(false);
            navigate(`/categoria/${cat.toLowerCase().replace(/ & /g,'-').replace(/ /g,'-')}`);
          }}>{cat}</span>
        ))}
        <button className="mobile-cta" onClick={() => { setMenuOpen(false); navigate({ pathname: '/', hash: '#contatti' }); }}>Contattaci Ora</button>
      </div>
    </>
  );
}
